/**
 * Cookie-authenticated API client with coordinated session refresh.
 *
 * Supabase access and refresh tokens are HttpOnly cookies. JavaScript never
 * reads or persists them. A 401 triggers one refresh and one request retry.
 * Web Locks serialize refresh-token rotation across same-origin tabs.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const REFRESH_LOCK_NAME = 'practice-meter-session-refresh';
const SESSION_EXPIRED_EVENT = 'auth:session-expired';
const SESSION_CHANGED_CHANNEL = 'practice-meter-auth';

const clearLegacyAuthData = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('access_token');
  sessionStorage.removeItem('refresh_token');
  sessionStorage.removeItem('user');
};

export class SessionRefreshUnavailableError extends Error {
  constructor(message = 'The session service is temporarily unavailable') {
    super(message);
    this.name = 'SessionRefreshUnavailableError';
  }
}

export class ApiClient {
  constructor({ fetchImpl, lockManager, broadcastChannel } = {}) {
    this.refreshPromise = null;
    this.fetchImpl = fetchImpl || ((...args) => window.fetch(...args));
    this.lockManager =
      lockManager === undefined ? window.navigator?.locks : lockManager;
    this.broadcastChannel =
      broadcastChannel === undefined && 'BroadcastChannel' in window
        ? new window.BroadcastChannel(SESSION_CHANGED_CHANNEL)
        : broadcastChannel;

    if (this.broadcastChannel) {
      this.broadcastChannel.onmessage = (event) => {
        if (event.data?.type === 'session-expired') {
          this.expireLocalSession({ broadcast: false });
        }
      };
    }
  }

  expireLocalSession({ broadcast = true } = {}) {
    clearLegacyAuthData();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    if (broadcast) {
      this.broadcastChannel?.postMessage({ type: 'session-expired' });
    }
  }

  async verifyCurrentSession() {
    let response;
    try {
      response = await this.fetchImpl(`${API_BASE_URL}/auth/me`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      const unavailable = new SessionRefreshUnavailableError();
      unavailable.cause = error;
      throw unavailable;
    }

    if (response.ok) {
      return true;
    }
    if (response.status === 401) {
      return false;
    }
    throw new SessionRefreshUnavailableError(
      `Session verification failed with status ${response.status}`
    );
  }

  async rotateRefreshToken() {
    // Another tab may have rotated the shared cookies while this tab waited
    // for the lock. Recheck before consuming the refresh token again.
    if (await this.verifyCurrentSession()) {
      return { ok: true, refreshed: false };
    }

    let response;
    try {
      response = await this.fetchImpl(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      const unavailable = new SessionRefreshUnavailableError();
      unavailable.cause = error;
      throw unavailable;
    }

    if (response.ok) {
      return { ok: true, refreshed: true };
    }
    if (response.status === 401 || response.status === 403) {
      this.expireLocalSession();
      return { ok: false, refreshed: false };
    }
    throw new SessionRefreshUnavailableError(
      `Session refresh failed with status ${response.status}`
    );
  }

  async refreshSession() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        if (this.lockManager?.request) {
          return await this.lockManager.request(
            REFRESH_LOCK_NAME,
            { mode: 'exclusive' },
            () => this.rotateRefreshToken()
          );
        }
        return await this.rotateRefreshToken();
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  async refreshToken() {
    const result = await this.refreshSession();
    return result.ok;
  }

  async fetch(url, options = {}) {
    const requestOptions = {
      ...options,
      credentials: 'include',
      headers: { ...(options.headers || {}) },
    };
    delete requestOptions._retry;

    let response = await this.fetchImpl(url, requestOptions);
    if (response.status !== 401 || options._retry) {
      return response;
    }

    const refreshResult = await this.refreshSession();
    if (!refreshResult.ok) {
      return response;
    }

    response = await this.fetchImpl(url, requestOptions);
    if (response.status === 401 && refreshResult.refreshed) {
      this.expireLocalSession();
    }
    return response;
  }

  async get(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.fetch(url, { ...options, method: 'GET' });
  }

  async post(endpoint, body, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.fetch(url, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }

  async put(endpoint, body, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.fetch(url, {
      ...options,
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: JSON.stringify(body),
    });
  }

  async delete(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;
    return this.fetch(url, { ...options, method: 'DELETE' });
  }

  async logout() {
    let remoteLogoutSucceeded = false;
    try {
      const response = await this.fetchImpl(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      remoteLogoutSucceeded = response.ok;
    } catch (error) {
      // Clearing the browser session must not depend on network availability.
      console.warn('Remote logout could not be confirmed:', error);
    } finally {
      // Local logout is authoritative even when the network is unavailable.
      this.expireLocalSession();
    }
    return remoteLogoutSucceeded;
  }
}

const apiClient = new ApiClient();
export default apiClient;

// Kept for callers that explicitly request a session refresh.
export const refreshAccessToken = () => apiClient.refreshToken();
